import React, { FC, useEffect, useState, useMemo } from 'react';
import { Box, Typography, MenuItem, Select, FormControl, Tooltip, Chip, Menu } from '@mui/material';
import PieChartIcon from '@mui/icons-material/PieChart';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { ProjectWithSites } from 'src/state-management/state';
import { Site } from 'src/api/Api';
import clientService from 'src/clientService';
import { formatDate } from 'src/util/date-utils';
import speciesCategorization from 'src/view/tool/bioacoustic/components/speciesCatalog/data/Species_categorization.json';

interface OverviewTabProps {
  projectDetails: ProjectWithSites;
  isSiteLevel: boolean;
  selectedSite: Site | null;
  bioSummary: Record<string, unknown> | null;
  overviewSummary: Record<string, unknown> | null;
  isTataMotors: boolean;
}

const metricBoxStyle = {
  background: `linear-gradient(145deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)`,
  border: `1px solid rgba(255,255,255,0.08)`,
  borderLeftWidth: `3px`,
  borderLeftStyle: `solid`,
  borderRadius: `12px`,
  padding: `16px`,
  display: `flex`,
  flexDirection: `column`,
  gap: `4px`,
  transition: `all 0.2s ease-in-out`,
};

const metricLabelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const metricValStyle = {
  fontSize: '26px',
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.1,
};

const OverviewTab: FC<OverviewTabProps> = ({
  projectDetails,
  isSiteLevel,
  selectedSite,
  bioSummary,
  overviewSummary,
  isTataMotors,
}) => {
  const [deployments, setDeployments] = useState<Record<string, any>[]>([]);
  const [mapMarkers, setMapMarkers] = useState<Record<string, any>[]>([]);
  const [totalSpecies, setTotalSpecies] = useState<number>(0);
  const [showSpeciesScheduleBreakdown, setShowSpeciesScheduleBreakdown] = useState<boolean>(false);
  const [expandedDeploymentsMap, setExpandedDeploymentsMap] = useState<Record<string, boolean>>({});
  const [selectedSchedule, setSelectedSchedule] = useState<'Schedule I' | 'Schedule II'>('Schedule I');
  const [scheduleMenuAnchor, setScheduleMenuAnchor] = useState<null | HTMLElement>(null);

  const schedule1Species = useMemo(
    () => speciesCategorization.filter((s) => s.wildlifeActClassification === 'Schedule I'),
    []
  );
  const schedule2Species = useMemo(
    () => speciesCategorization.filter((s) => s.wildlifeActClassification === 'Schedule II'),
    []
  );
  const activeScheduleSpecies = selectedSchedule === 'Schedule I' ? schedule1Species : schedule2Species;

  // State for deployment switching in Site level
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string>('all');

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      // 1. Fetch map markers immediately for fast rendering of Number of Deployments
      const markersPromise = clientService.request<Record<string, any>[], unknown>({
        path: `/api/v1/projects/${projectDetails.project_id}/map/markers`,
        method: 'GET',
        query: { bbox: `-180,-90,180,90`, _t: Date.now() },
        format: 'json',
        secure: true,
      }).then(markersRes => {
        if (!isMounted) return;
        let markersData = markersRes.data || [];
        if (isSiteLevel && selectedSite) {
          markersData = markersData.filter((m: Record<string, any>) => m.site_id === selectedSite.site_id);
        }
        setMapMarkers(markersData);
      }).catch(err => {
        console.error('Failed to fetch markers for overview', err);
      });

      // 2. Fetch deployments in parallel without blocking map markers
      try {
        const allDepsRes = await clientService.api.getAllDeploymentsApiV1AdminDeploymentsGet();
        if (!isMounted) return;

        let projDeps = (allDepsRes.data || []).filter((d: Record<string, any>) => d.project_id === projectDetails?.project_id);
        if (isSiteLevel && selectedSite) {
          projDeps = projDeps.filter((d: Record<string, any>) => d.site_id === selectedSite.site_id);
        }
        setDeployments(projDeps);

        // Fetch species per deployment only when not Tata Motors project
        if (!isTataMotors) {
          const speciesPromises = projDeps.map((d: Record<string, any>) =>
            clientService.request<Record<string, unknown[]>, unknown>({
              path: `/api/v1/deployments/${d.deployment_id}/biodiversity/audio-clips`,
              method: 'GET',
              format: 'json',
              secure: true,
            }).then(res => Object.keys(res.data || {}).length).catch(() => 0)
          );

          const speciesCounts = await Promise.all(speciesPromises);
          if (isMounted) {
            setTotalSpecies(speciesCounts.reduce((a, b) => a + b, 0));
          }
        }
      } catch (err) {
        console.error('Failed to fetch overview deployments', err);
      }

      await markersPromise;
    };

    if (projectDetails?.project_id) {
      fetchData();
    }

    return () => { isMounted = false; };
  }, [projectDetails?.project_id, isSiteLevel, selectedSite, isTataMotors]);

  // Process Metrics
  const activeDeployments = selectedDeploymentId === 'all' 
    ? deployments 
    : deployments.filter(d => d.deployment_id === selectedDeploymentId);

  const activeMarkers = useMemo(() => {
    if (selectedDeploymentId === 'all') return mapMarkers;
    return mapMarkers.filter(m => m.deployment_id === selectedDeploymentId);
  }, [mapMarkers, selectedDeploymentId]);

  const [deploymentViewCategory, setDeploymentViewCategory] = useState<'all' | 'visual' | 'acoustic' | 'soil'>('all');

  const subCategoryBreakdown = useMemo(() => {
    const visualCameras: Record<string, any>[] = [];
    const visualAquatic: Record<string, any>[] = [];
    const acousticBio: Record<string, any>[] = [];
    const acousticHydro: Record<string, any>[] = [];
    const soilSensors: Record<string, any>[] = [];

    activeMarkers.forEach((m: Record<string, any>) => {
      const type = (m.device_type || 'default').toLowerCase();
      const dName = m.deployment_name;
      const dId = m.deployment_id;

      const isCamera = (type === 'camera' || dName === 'PMP-T0171' || dName === 'PMP-T0612') && dName !== 'PMP-T0352';
      const isAquaticCamera = type === 'aquatic_camera' || dName === 'PMP-T0352';
      const isBio = (type === 'audio' || type === 'bioacoustic') && dId !== '462fb893-ec5b-429e-a470-9744b0493ab9' && dId !== '6621d433-48bb-45a6-94b6-84948f136878';
      const isHydro = type === 'hydro' || type === 'hydroacoustic' || dId === '462fb893-ec5b-429e-a470-9744b0493ab9' || dId === '6621d433-48bb-45a6-94b6-84948f136878';
      const isSoil = type === 'soil_sensor' || type === 'soil';

      if (isCamera) {
        visualCameras.push(m);
      } else if (isAquaticCamera) {
        visualAquatic.push(m);
      } else if (isBio) {
        acousticBio.push(m);
      } else if (isHydro) {
        acousticHydro.push(m);
      } else if (isSoil) {
        soilSensors.push(m);
      }
    });

    return {
      visual: {
        name: 'Visual monitoring',
        total: visualCameras.length + visualAquatic.length,
        color: '#38bdf8',
        items: [
          { name: 'Terrestrial Cameras', count: visualCameras.length, devices: visualCameras, color: '#38bdf8' },
          { name: 'Aquatic Cameras', count: visualAquatic.length, devices: visualAquatic, color: '#0284c7' },
        ].filter(i => i.count > 0)
      },
      acoustic: {
        name: 'Acoustic Monitoring',
        total: acousticBio.length + acousticHydro.length,
        color: '#818cf8',
        items: [
          { name: 'Bioacoustic Sensors', count: acousticBio.length, devices: acousticBio, color: '#818cf8' },
          { name: 'Hydroacoustic Sensors', count: acousticHydro.length, devices: acousticHydro, color: '#6366f1' },
        ].filter(i => i.count > 0)
      },
      soil: {
        name: 'Soil monitoring',
        total: soilSensors.length,
        color: '#34d399',
        items: [
          { name: 'Soil Sensors', count: soilSensors.length, devices: soilSensors, color: '#34d399' }
        ].filter(i => i.count > 0)
      }
    };
  }, [activeMarkers]);

  const totalDeploymentsCount = subCategoryBreakdown.visual.total + subCategoryBreakdown.acoustic.total + subCategoryBreakdown.soil.total;

  // Chart Options for Deployments
  const deviceChartOptions = useMemo(() => {
    let data: { name: string; y: number; color: string }[] = [];
    let seriesName = 'Deployments';

    if (deploymentViewCategory === 'all') {
      data = [
        { name: 'Visual monitoring', y: subCategoryBreakdown.visual.total, color: '#38bdf8' },
        { name: 'Acoustic Monitoring', y: subCategoryBreakdown.acoustic.total, color: '#818cf8' },
        { name: 'Soil monitoring', y: subCategoryBreakdown.soil.total, color: '#34d399' },
      ].filter(d => d.y > 0);
    } else {
      seriesName = subCategoryBreakdown[deploymentViewCategory].name;
      data = subCategoryBreakdown[deploymentViewCategory].items.map(item => ({
        name: item.name,
        y: item.count,
        color: item.color,
      }));
    }

    return {
      accessibility: { enabled: false },
      exporting: { enabled: false },
      credits: { enabled: false },
      chart: { 
        type: 'pie', 
        backgroundColor: 'transparent', 
        height: 160,
        animation: { duration: 500 }
      },
      title: { text: null },
      plotOptions: {
        pie: {
          innerSize: '60%',
          borderWidth: 0,
          cursor: 'pointer',
          animation: { duration: 500 },
          point: {
            events: {
              click: function (this: { name: string }) {
                if (deploymentViewCategory === 'all') {
                  if (this.name === 'Visual monitoring') setDeploymentViewCategory('visual');
                  else if (this.name === 'Acoustic Monitoring') setDeploymentViewCategory('acoustic');
                  else if (this.name === 'Soil monitoring') setDeploymentViewCategory('soil');
                }
              }
            }
          },
          dataLabels: { enabled: false },
          showInLegend: true
        }
      },
      legend: {
        itemStyle: { color: '#fff', fontSize: '10px' },
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },
      series: [{ name: seriesName, data, animation: { duration: 500 } }]
    };
  }, [deploymentViewCategory, subCategoryBreakdown]);

  // Chart Options for Species Schedule Breakdown (Tata Motors)
  const speciesScheduleChartOptions = useMemo(() => {
    const notScheduledCount = speciesCategorization.filter((s) => s.wildlifeActClassification.includes('Not located')).length;
    const totalCount = speciesCategorization.length;
    const data = [
      { name: 'Schedule I', y: schedule1Species.length, color: '#f59e0b' },
      { name: 'Schedule II', y: schedule2Species.length, color: '#38bdf8' },
      { name: 'Not in Schedule', y: notScheduledCount, color: '#94a3b8' },
    ];

    return {
      accessibility: { enabled: false },
      exporting: { enabled: false },
      credits: { enabled: false },
      chart: { type: 'pie', backgroundColor: 'transparent', height: 160 },
      title: { text: null },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        style: { color: '#fff', fontSize: '11px' },
        formatter: function (this: any) {
          const pct = ((this.y / totalCount) * 100).toFixed(1);
          return `<b>${this.point.name}</b>: ${this.y} species (${pct}%)`;
        }
      },
      plotOptions: {
        pie: {
          innerSize: '60%',
          borderWidth: 0,
          dataLabels: { enabled: false },
          showInLegend: true
        }
      },
      legend: {
        itemStyle: { color: '#fff', fontSize: '10px' },
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },
      series: [{ name: 'Species', data }]
    };
  }, [schedule1Species.length, schedule2Species.length]);

  // Chart Options for IUCN Red List Category Breakdown
  const iucnCategoryChartOptions = useMemo(() => {
    const categoryMeta: Record<string, { label: string; color: string }> = {
      LC: { label: 'Least Concern (LC)', color: '#10b981' },
      NT: { label: 'Near Threatened (NT)', color: '#06b6d4' },
      VU: { label: 'Vulnerable (VU)', color: '#f59e0b' },
      EN: { label: 'Endangered (EN)', color: '#f97316' },
      CR: { label: 'Critically Endangered (CR)', color: '#ef4444' },
      DD: { label: 'Data Deficient (DD)', color: '#94a3b8' },
      NE: { label: 'Not Evaluated (NE)', color: '#64748b' },
    };

    const counts: Record<string, number> = {};
    speciesCategorization.forEach((s) => {
      const code = s.globalIucnRedListCategory || 'NE';
      counts[code] = (counts[code] || 0) + 1;
    });

    const totalCount = speciesCategorization.length;
    const data = Object.entries(counts).map(([code, count]) => {
      const meta = categoryMeta[code] || { label: code, color: '#94a3b8' };
      return {
        name: meta.label,
        y: count,
        color: meta.color,
      };
    });

    return {
      accessibility: { enabled: false },
      exporting: { enabled: false },
      credits: { enabled: false },
      chart: { type: 'pie', backgroundColor: 'transparent', height: 160 },
      title: { text: null },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        style: { color: '#fff', fontSize: '11px' },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const yVal = this.y ?? 0;
          const pct = totalCount > 0 ? ((yVal / totalCount) * 100).toFixed(1) : '0';
          return `<b>${this.point.name}</b>: ${yVal} species (${pct}%)`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: '60%',
          borderWidth: 0,
          dataLabels: { enabled: false },
          showInLegend: true,
        },
      },
      legend: {
        itemStyle: { color: '#fff', fontSize: '10px' },
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle',
      },
      series: [{ name: 'IUCN Categories', data }],
    };
  }, []);

  // Cycles calculation
  const completedCycles = isTataMotors 
    ? 3 
    : deployments.filter(d => d.end_date && new Date(d.end_date) < new Date()).length;
  const upcomingCycle = deployments.find(d => new Date(d.start_date) > new Date());
  const upcomingCycleText = isTataMotors 
    ? formatDate(new Date(2026, 9, 21)) 
    : (upcomingCycle ? formatDate(upcomingCycle.start_date) : 'None');

  const now = new Date();
  const ongoingDeployment = deployments.find(d => {
    if (!d.start_date) return false;
    const start = new Date(d.start_date);
    const end = d.end_date ? new Date(d.end_date) : null;
    return start <= now && (!end || end >= now);
  });
  const ongoingCycleText = isTataMotors 
    ? `${formatDate(new Date(2026, 9, 12))} - ${formatDate(new Date(2026, 9, 19))}` 
    : (ongoingDeployment 
        ? `${formatDate(ongoingDeployment.start_date)} - ${formatDate(ongoingDeployment.end_date)}`
        : 'None');

  // Data Collected (Hardcoded for Tata Motors project only)
  const totalAudioFiles = isTataMotors 
    ? 60394 
    : (activeDeployments.reduce((acc, d) => acc + (d.file_count || 0), 0) || 0);
  const totalImages = isTataMotors 
    ? 492 
    : ((overviewSummary as Record<string, any>)?.total_images as number || 0);
  const totalEDNA = isTataMotors 
    ? 4 
    : ((overviewSummary as Record<string, any>)?.total_edna_samples as number || 0);
  const totalSoil = isTataMotors 
    ? 10 
    : subCategoryBreakdown.soil.total;

  // Species of Concern (Mocked)
  const speciesOfConcern = isSiteLevel ? [
    { name: 'Panthera tigris', level: 'High', location: activeDeployments[0]?.deployment_name || 'Deployment 01' },
    { name: 'Elephas maximus', level: 'High', location: activeDeployments[1]?.deployment_name || 'Deployment 02' }
  ] : [
    { name: 'Panthera tigris', level: 'High', location: projectDetails?.sites?.[0]?.site_name || 'Site A' },
    { name: 'Rhinoceros unicornis', level: 'High', location: projectDetails?.sites?.[1]?.site_name || 'Site B' }
  ];

  return (
    <Box className="insights-card" sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      
      {!isSiteLevel && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <Box sx={{ ...metricBoxStyle, flex: 1, borderLeftColor: 'rgba(255,255,255,0.2)' }}>
            <Typography sx={metricLabelStyle}>Number of Sites</Typography>
            <Typography sx={metricValStyle}>{projectDetails?.sites?.length || 0}</Typography>
          </Box>
          <Box sx={{ ...metricBoxStyle, flex: 1, borderLeftColor: '#8b5cf6' }}>
            <Typography sx={metricLabelStyle}>Cycles Completed</Typography>
            <Typography sx={metricValStyle}>{completedCycles}</Typography>
          </Box>
        </Box>
      )}

      {isSiteLevel && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <Box sx={{ ...metricBoxStyle, flex: 1, borderLeftColor: '#10b981' }}>
            <Typography sx={metricLabelStyle}>Site Health</Typography>
            <Typography sx={{ ...metricValStyle, color: '#10b981' }}>Healthy</Typography>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <FormControl size="small" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}>
              <Select
                value={selectedDeploymentId}
                onChange={(e) => setSelectedDeploymentId(e.target.value)}
                displayEmpty
                sx={{ fontSize: '12px' }}
              >
                <MenuItem value="all">All Deployments</MenuItem>
                {deployments.map(d => (
                  <MenuItem key={d.deployment_id} value={d.deployment_id}>{d.deployment_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      <Box sx={{ ...metricBoxStyle, borderLeftColor: '#3b82f6' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={metricLabelStyle}>
            {deploymentViewCategory === 'all'
              ? `Number of Deployments (${totalDeploymentsCount})`
              : `${subCategoryBreakdown[deploymentViewCategory].name} (${subCategoryBreakdown[deploymentViewCategory].total})`}
          </Typography>
          {deploymentViewCategory !== 'all' && (
            <Typography
              onClick={() => setDeploymentViewCategory('all')}
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#38bdf8',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              ← Overview
            </Typography>
          )}
        </Box>

        {/* Category Filter Pills */}
        <Box sx={{ display: 'flex', gap: '6px', mt: 1, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
          {[
            { id: 'all', label: 'All', count: totalDeploymentsCount, color: '#fff' },
            { id: 'visual', label: 'Visual', count: subCategoryBreakdown.visual.total, color: '#38bdf8' },
            { id: 'acoustic', label: 'Acoustic', count: subCategoryBreakdown.acoustic.total, color: '#818cf8' },
            { id: 'soil', label: 'Soil', count: subCategoryBreakdown.soil.total, color: '#34d399' },
          ].map(cat => {
            const active = deploymentViewCategory === cat.id;
            return (
              <Box
                key={cat.id}
                onClick={() => setDeploymentViewCategory(cat.id as any)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  px: 1.25,
                  py: 0.35,
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: active ? `${cat.color}22` : 'rgba(255, 255, 255, 0.04)',
                  color: active ? cat.color : 'rgba(255, 255, 255, 0.7)',
                  border: `1px solid ${active ? cat.color : 'rgba(255, 255, 255, 0.08)'}`,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderColor: cat.color,
                    color: '#fff',
                  }
                }}
              >
                <span>{cat.label}</span>
                <span style={{ fontSize: '9px', opacity: 0.8 }}>({cat.count})</span>
              </Box>
            );
          })}
        </Box>

        {totalDeploymentsCount > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <HighchartsReact 
              highcharts={Highcharts} 
              options={deviceChartOptions} 
              updateArgs={[true, true, { duration: 500 }]}
            />
          </Box>
        )}

        {/* Subcategory Details Breakdown */}
        {deploymentViewCategory !== 'all' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: 1 }}>
            {subCategoryBreakdown[deploymentViewCategory].items.map((sub, idx) => (
              <Box
                key={idx}
                sx={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: `1px solid ${sub.color}33`,
                  borderLeft: `3px solid ${sub.color}`,
                  borderRadius: '8px',
                  p: 1.2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>
                    {sub.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: sub.color, background: `${sub.color}20`, px: 1, py: 0.2, borderRadius: '4px' }}>
                    {sub.count} {sub.count === 1 ? 'Deployment' : 'deployments'}
                  </Typography>
                </Box>
                {/* Device tags with Show More if > 2 */}
                {(() => {
                  const isExpanded = !!expandedDeploymentsMap[sub.name];
                  const hasMore = sub.devices.length > 2;
                  const visibleDevices = (hasMore && !isExpanded) ? sub.devices.slice(0, 2) : sub.devices;

                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                      {visibleDevices.map((d, dIdx) => (
                        <Box
                          key={dIdx}
                          sx={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                            px: 0.75,
                            py: 0.2,
                            fontSize: '10px',
                            fontWeight: 600,
                            color: 'rgba(255, 255, 255, 0.85)',
                          }}
                        >
                          {d.deployment_name || d.device_name || `Dev #${dIdx + 1}`}
                        </Box>
                      ))}

                      {hasMore && (
                        <Typography
                          component="span"
                          onClick={() => setExpandedDeploymentsMap(prev => ({ ...prev, [sub.name]: !isExpanded }))}
                          sx={{
                            display: 'inline-block',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 500,
                            color: sub.color || '#38bdf8',
                            ml: 0.5,
                            userSelect: 'none',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                              color: '#fff',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {isExpanded ? 'Show less' : '....Show more'}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: '12px' }}>
        <Box sx={{ ...metricBoxStyle, flex: 1, borderLeftColor: '#10b981' }}>
          <Typography sx={metricLabelStyle}>Ongoing Deployment Cycle</Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', mt: 1 }}>
            {ongoingCycleText}
          </Typography>
        </Box>
        <Box sx={{ ...metricBoxStyle, flex: 1, borderLeftColor: '#f59e0b' }}>
          <Typography sx={metricLabelStyle}>Upcoming Cycle</Typography>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', mt: 1 }}>
            {upcomingCycleText}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ ...metricBoxStyle, borderLeftColor: '#f59e0b' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={metricLabelStyle}>
            Total Species Identified
          </Typography>
          {isTataMotors && (
            <Tooltip title={showSpeciesScheduleBreakdown ? "Hide Schedule Breakdown" : "View Schedule Breakdown"} arrow placement="top">
              <Box
                onClick={() => setShowSpeciesScheduleBreakdown(prev => !prev)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: showSpeciesScheduleBreakdown ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: `1px solid ${showSpeciesScheduleBreakdown ? '#f59e0b' : 'rgba(255, 255, 255, 0.1)'}`,
                  color: showSpeciesScheduleBreakdown ? '#f59e0b' : 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '6px',
                  p: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(245, 158, 11, 0.25)',
                    color: '#f59e0b',
                    borderColor: '#f59e0b',
                  }
                }}
              >
                <PieChartIcon sx={{ fontSize: '16px' }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Typography sx={metricValStyle}>
          {isTataMotors ? 73 : totalSpecies}
        </Typography>

        {/* When expanded for Tata Motors: Show Donut Chart */}
        {isTataMotors && showSpeciesScheduleBreakdown && (
          <Box sx={{ mt: 1 }}>
            <HighchartsReact highcharts={Highcharts} options={speciesScheduleChartOptions} />
          </Box>
        )}
      </Box>

      <Box sx={{ ...metricBoxStyle, borderLeftColor: '#10b981' }}>
        <Typography sx={{ ...metricLabelStyle, mb: 1.5 }}>Data Collected</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          <DataTile label="Audio Files" value={totalAudioFiles} />
          <DataTile label="Images" value={totalImages} />
          <DataTile label="eDNA Samples" value={totalEDNA} />
          <DataTile label="Soil Samples" value={totalSoil} />
        </Box>
      </Box>

      {isTataMotors ? (
        <>
        <Box sx={{ ...metricBoxStyle, borderLeftColor: '#10b981' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={metricLabelStyle}>
              SPECIES ({activeScheduleSpecies.length})
            </Typography>
            <Chip
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{selectedSchedule}</span>
                  <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)' }} />
                </Box>
              }
              onClick={(e) => setScheduleMenuAnchor(e.currentTarget)}
              size="small"
              sx={{
                cursor: 'pointer',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid #1E2E25',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '11px',
                height: '24px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderColor: '#10B981',
                },
              }}
            />
            <Menu
              anchorEl={scheduleMenuAnchor}
              open={Boolean(scheduleMenuAnchor)}
              onClose={() => setScheduleMenuAnchor(null)}
              PaperProps={{
                sx: {
                  bgcolor: '#0f172a',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  color: '#fff',
                  minWidth: '150px',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.6)',
                  '& .MuiMenuItem-root': {
                    fontSize: '12px',
                    fontWeight: 500,
                    gap: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: '6px',
                    px: '12px',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(16, 185, 129, 0.15)',
                      color: '#10B981',
                      fontWeight: 700,
                    },
                  },
                },
              }}
            >
              <MenuItem
                selected={selectedSchedule === 'Schedule I'}
                onClick={() => {
                  setSelectedSchedule('Schedule I');
                  setScheduleMenuAnchor(null);
                }}
              >
                <span>Schedule I</span>
                <Box
                  component="span"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    px: '6px',
                    py: '1px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: '#10B981',
                  }}
                >
                  {schedule1Species.length}
                </Box>
              </MenuItem>
              <MenuItem
                selected={selectedSchedule === 'Schedule II'}
                onClick={() => {
                  setSelectedSchedule('Schedule II');
                  setScheduleMenuAnchor(null);
                }}
              >
                <span>Schedule II</span>
                <Box
                  component="span"
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    px: '6px',
                    py: '1px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: '#10B981',
                  }}
                >
                  {schedule2Species.length}
                </Box>
              </MenuItem>
            </Menu>
          </Box>

          {/* Species Chips (commonName only) */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              maxHeight: '260px',
              overflowY: 'auto',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {activeScheduleSpecies.map((sp) => (
              <Chip
                key={sp.commonName}
                label={sp.commonName}
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  color: '#FFFFFF',
                  fontSize: '11.5px',
                  fontWeight: 500,
                  height: '28px',
                  borderRadius: '6px',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(16, 185, 129, 0.12)',
                    borderColor: 'rgba(16, 185, 129, 0.4)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* IUCN Category Card with Pie Chart */}
        <Box sx={{ ...metricBoxStyle, borderLeftColor: '#10b981' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={metricLabelStyle}>IUCN Category</Typography>
            <Box sx={{
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '10px',
              px: 1,
              py: 0.2,
            }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#10b981' }}>
                {speciesCategorization.length} Species
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <HighchartsReact highcharts={Highcharts} options={iucnCategoryChartOptions} />
          </Box>
        </Box>
        </>
      ) : (
        <Box sx={{ ...metricBoxStyle, borderLeftColor: '#ef4444' }}>
          <Typography sx={{ ...metricLabelStyle, mb: 1 }}>Species of Concern</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {speciesOfConcern.map((sp, idx) => (
              <Box key={idx} sx={{ background: 'rgba(255,255,255,0.03)', p: 1.5, borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>{sp.name}</Typography>
                  <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>{sp.location}</Typography>
                </Box>
                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: sp.level === 'High' ? '#ef4444' : '#f59e0b', background: sp.level === 'High' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)', px: 1, py: 0.5, borderRadius: '4px' }}>
                  {sp.level} CONCERN
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const DataTile = ({ label, value }: { label: string, value: number | string }) => (
  <Box sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px', p: 1.5, display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>{value.toLocaleString()}</Typography>
  </Box>
);

export default OverviewTab;
